import React, { useCallback, useEffect, useRef, useState } from "react";
import Icons from "../../icons";
import { JiraStatusBadge } from "../../jiraBadges";
import { ActivityButton } from "../ActivityPanel";
import * as Loader from "../Loader";
import ErrorDialog from "../ErrorDialog";
import ExecutionResultView from "../ExecutionResultView";
import JiraConnectDialog from "../JiraConnectDialog";
import RunVariablesDialog from "../RunVariablesDialog";
import { IconButton, LiveRunModal } from "../TestRunsPanel";
import TestRunSubmitPanel from "./TestRunSubmitPanel";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* The execution's version fields Execute asks for when Jira has none yet.
   Planned version is what Fix version used to be (Finish needs it); Affects
   and Fix are optional. Each takes several versions, separated by commas. */
const SEVERAL = "One or more, separated by commas, e.g. PlantOPS 1.0.0, PlantOPS 1.1.0";
const VERSION_FIELDS = [
  { name: "planned_versions", label: "Planned version", hint: SEVERAL },
  { name: "affects_versions", label: "Affects version", hint: SEVERAL, optional: true },
  { name: "fix_versions", label: "Fix version", hint: SEVERAL, optional: true },
];

export const versionList = (text) => [...new Set(String(text || "").split(",").map((v) => v.trim()).filter(Boolean))];

/*
 * For you › Test Runs — every Xray test run assigned to the signed-in user (and
 * the unassigned runs of executions they own), grouped by Test Execution.
 *
 * A run is one Test inside an existing Test Execution. Each row says whether it
 * can be executed and why not, and offers one next step: Create artifact,
 * ▶ Execute, Watch, Review & submit, Retry submit — or nothing (final). The
 * result is written to THAT run only when the user submits it; nothing new is
 * created in Jira. The execution's assignee can Finish it once every run is
 * final. (The For you Executions tab and the Artifacts screen are separate.)
 */

/* "To do" (the default) is everything still waiting on you: runs to execute
   and results to review and submit. A run that finished in Cortex stays
   EXECUTING in Jira until it is submitted, so it must not drop out of sight. */
const FILTERS = [
  { id: "todo", label: "To do" },
  { id: "ready", label: "Ready to submit" },
  { id: "submitted", label: "Submitted" },
  { id: "all", label: "All" },
];

const RUN_TONE = {
  "TO DO": "bg-slate-100 text-slate-600 ring-slate-500/20",
  EXECUTING: "bg-blue-50 text-blue-700 ring-blue-600/20",
  PASSED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  FAILED: "bg-rose-50 text-rose-700 ring-rose-600/20",
  BLOCKED: "bg-amber-50 text-amber-800 ring-amber-600/30",
};

async function getJson(url, init) {
  const res = await fetch(url, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = body?.detail;
    const err = new Error((typeof d === "string" ? d : d?.message) || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return body?.data;
}

function RunChip({ status, local }) {
  /* A result waiting to be submitted reads as that result, marked so. */
  const pending = local?.submit_state === "ready" || local?.submit_state === "error";
  const label = pending ? `${local.run_result} · not submitted` : status;
  const tone = RUN_TONE[pending ? local.run_result : status] || RUN_TONE["TO DO"];
  return <span className={`inline-flex flex-none rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ring-1 ring-inset ${tone}`}>{label}</span>;
}

/* The list right after a run was submitted, before Jira is read again: the
   run shows its result as final and submitted, moves out of "To do" / "Ready
   to submit", and its execution's progress counts it. Its execution stays
   listed (its Finish may now be due); the refresh that follows settles it. */
export function afterSubmit(data, runId, filter) {
  if (!data) return data;
  let found = false;
  const groups = (data.groups || []).map((g) => {
    const rows = [];
    let progress = g.progress;
    for (const r of g.rows) {
      if (r.run.id !== runId) {
        rows.push(r);
        continue;
      }
      found = true;
      const result = r.local?.run_result || r.run.status;
      if (progress && !["PASSED", "FAILED", "BLOCKED"].includes(r.run.status)) {
        progress = {
          ...progress,
          done: progress.done + 1,
          failed: (progress.failed || 0) + (result === "FAILED" ? 1 : 0),
          blocked: (progress.blocked || 0) + (result === "BLOCKED" ? 1 : 0),
        };
      }
      if (filter === "submitted" || filter === "all") {
        rows.push({
          ...r,
          run: { ...r.run, status: result },
          local: { ...(r.local || {}), submit_state: "submitted", submit_error: "" },
          action: "view",
          bucket: "submitted",
          can_execute: false,
          blocked_reason: "",
        });
      }
    }
    return { ...g, rows, progress };
  });
  if (!found) return data;
  const counts = { ...(data.counts || {}) };
  for (const k of ["todo", "ready"]) if (counts[k]) counts[k] -= 1;
  counts.submitted = (counts.submitted || 0) + 1;
  return { ...data, groups, counts };
}

/* A Jira date-time as people read it: "25 Oct 2026, 14:37". */
export function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/* The progress line of an execution: every run in it (anyone's) while it is
   open; only the runs listed here once it is closed. */
export function progressText(p) {
  if (!p) return "";
  const parts = [`${p.done} of ${p.total} done`];
  if (p.failed) parts.push(`${p.failed} failed`);
  if (p.blocked) parts.push(`${p.blocked} blocked`);
  return parts.join(" · ") + (p.scope === "yours" ? " (your runs)" : "");
}

/* Who the run is for, and who ran it when. */
function runFacts(run) {
  const facts = [];
  if (run.assigned_to_me) facts.push("Assigned to you");
  else if (run.covered_as_owner) facts.push("Unassigned · yours as the execution's assignee");
  else if (run.assignee) facts.push(`Assigned to ${run.assignee}`);
  const by = run.executed_by_me ? "you" : run.executed_by;
  if (by) facts.push(`Executed by ${by}`);
  if (run.started_on) facts.push(`Started ${when(run.started_on)}`);
  if (run.finished_on) facts.push(`Finished ${when(run.finished_on)}`);
  return facts;
}

const META_CHIP = "inline-flex flex-none rounded-full bg-slate-100 px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-600";

function RunRow({ row, busy, onAction, last = false }) {
  const { test, run, local, action } = row;
  const final = ["PASSED", "FAILED", "BLOCKED"].includes(run.status);
  const facts = runFacts(run);
  /* The chip already says a final run's result; its reason would repeat it. */
  const reason = final && /a result is final/.test(row.blocked_reason || "") ? "" : row.blocked_reason;
  const active = local && ["queued", "running"].includes(local.run_status);
  let button = null;
  if (action === "execute") {
    button = (
      <button
        type="button"
        onClick={() => onAction("execute", row)}
        disabled={!!busy}
        aria-label={`Execute ${test.key}`}
        title="Execute this run — its result waits for you to review and submit"
        className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gradient-to-b from-emerald-500 to-emerald-600 text-white shadow-sm shadow-emerald-600/30 ring-1 ring-inset ring-emerald-700/20 transition hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50"
      >
        <Icons.Play className="ml-0.5 h-4 w-4" />
      </button>
    );
  } else if (action === "create_artifact") {
    button = (
      <button
        type="button"
        onClick={() => onAction("create_artifact", row)}
        disabled={!!busy}
        className="inline-flex items-center gap-1.5 rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-sm font-semibold text-brand-700 hover:bg-brand-100 disabled:opacity-50"
      >
        <Icons.Plus className="h-4 w-4" /> Create artifact
      </button>
    );
  } else if (action === "watch" || active) {
    button = (
      <IconButton title="Watch the live browser" onClick={() => onAction("watch", row)}>
        <Icons.Activity className="h-4 w-4" />
      </IconButton>
    );
  } else if (action === "review" || action === "retry_submit") {
    button = (
      <button
        type="button"
        onClick={() => onAction("review", row)}
        className={[
          "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold shadow-sm",
          action === "retry_submit" ? "border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100" : "bg-brand-600 text-white hover:bg-brand-700",
        ].join(" ")}
      >
        {action === "retry_submit" ? "Retry submit" : "Review & submit"}
      </button>
    );
  } else if (local?.executionkey) {
    button = (
      <IconButton title="See the result, with each step's screenshots" onClick={() => onAction("view", row)}>
        <Icons.Eye className="h-4 w-4" />
      </IconButton>
    );
  }
  return (
    <li aria-label={`Run of ${test.key}`} className="relative flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 pl-14 pr-4">
      {/* Tree branch from the Test Execution above (as on the Executions tab):
          the trunk drops from under its icon and runs on past every test but
          the last, which ends in an elbow; the test sits a level further in. */}
      <span aria-hidden="true" className={`absolute left-6 top-0 w-px bg-slate-300 ${last ? "h-[1.4rem]" : "bottom-0"}`} />
      <span aria-hidden="true" className="absolute left-6 top-[1.4rem] h-px w-5 bg-slate-300" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <a href={test.url || undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline">
            <Icons.JiraXrayTest className="h-4 w-4" />
            {test.key}
          </a>
          <JiraStatusBadge status={test.status} category={test.status_category} />
          {test.risk && <span className={META_CHIP}>Risk: {test.risk}</span>}
          {run.test_type && <span className={META_CHIP}>{run.test_type}</span>}
          <RunChip status={run.status} local={local} />
        </div>
        <p className="mt-0.5 text-sm text-slate-700">{test.summary}</p>
        {facts.length > 0 && <p className="mt-0.5 text-xs text-slate-500">{facts.join(" · ")}</p>}
        {reason && !["review", "retry_submit", "watch"].includes(action) && (
          <p className="mt-1 text-xs text-slate-500">{reason}</p>
        )}
      </div>
      <div className="flex flex-none items-center gap-2">
        {final && run.url && (
          <a
            href={run.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
          >
            Open in Jira <Icons.Link className="h-3.5 w-3.5" />
          </a>
        )}
        {/* Who did what on this run (formal entries, and its Artifact's). */}
        {run.id && <ActivityButton compact title={`${test.key} in ${row.execution?.key || "its Test Execution"}`} scope={{ xray_test_run_id: run.id }} />}
        {button}
      </div>
    </li>
  );
}

function Group({ g, busyRun, onAction, onFinish }) {
  const te = g.execution;
  const p = g.progress;
  return (
    <li className="overflow-hidden rounded-lg border border-slate-200 bg-card shadow-sm">
      <div className="flex flex-wrap items-start gap-3 px-4 pb-1 pt-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <a href={te.url || undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline">
              <Icons.JiraTestExecution className="h-4 w-4" />
              {te.key}
            </a>
            <JiraStatusBadge status={te.status} category={te.status_category} />
            {te.risk && <span className="text-[0.6875rem] font-semibold text-slate-600">Risk: {te.risk}</span>}
            <span className="text-xs text-slate-500">{progressText(p)}</span>
          </div>
          <p className="mt-1 text-sm text-slate-800">{te.summary}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {te.test_plan_key && (
              <>
                Test Plan:{" "}
                <a href={te.test_plan_url || undefined} target="_blank" rel="noreferrer" className="font-semibold text-blue-700 hover:underline">
                  {te.test_plan_key}
                </a>{" "}
                ·{" "}
              </>
            )}
            Planned version: {(te.planned_versions || []).join(", ") || "—"} · Affects version:{" "}
            {(te.affects_versions || []).join(", ") || "—"} · Fix version: {(te.fix_versions || []).join(", ") || "—"} ·
            Build: {te.build_number || "—"} · Environment:{" "}
            {(te.test_environments || []).join(", ") || "—"}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Assignee: {te.assignee || "—"} · Executed By: {te.executed_by || "—"}
          </p>
        </div>
        {g.can_finish && (
          <div className="flex flex-none flex-col items-end gap-1">
            <button
              type="button"
              onClick={() => onFinish(te)}
              disabled={(g.finish_missing || []).length > 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            >
              <Icons.Check className="h-4 w-4" /> Finish execution
            </button>
            {(g.finish_missing || []).length > 0 && (
              <span className="text-[0.6875rem] text-slate-500">Fill {g.finish_missing.join(", ")} in Jira first.</span>
            )}
          </div>
        )}
      </div>
      <ul aria-label={`Test runs in ${te.key}`} className="pb-2">
        {g.rows.map((r, i) => (
          <RunRow key={r.run.id} row={r} last={i === g.rows.length - 1} busy={busyRun === r.run.id} onAction={onAction} />
        ))}
      </ul>
    </li>
  );
}

/* The API answers 428 when an action needs the user's own Jira token (none
   saved, or Jira refused it): ask for it, then do the same action again. */
const NEEDS_TOKEN = 428;

export default function ForYouTestRuns({ userkey, onLinkChange, onCreateArtifact, refreshSeq = 0 }) {
  const [filter, setFilter] = useState("todo");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // {run, label, sublabel}
  const [runVars, setRunVars] = useState(null); // row waiting for its values
  const [watching, setWatching] = useState(null);
  const [review, setReview] = useState(null); // row being reviewed
  const [failure, setFailure] = useState(null);
  const [tokenAsk, setTokenAsk] = useState(null); // {reason, retry}

  /* A failed action: ask for the token when that is why, else say why. */
  const failed = (e, title, retry) => {
    if (e.status === NEEDS_TOKEN) setTokenAsk({ reason: e.message, retry });
    else setFailure({ title, message: e.message });
  };

  /* Connect Jira (email + API token), then the same action again. */
  const tokenDialog = tokenAsk && (
    <JiraConnectDialog
      userkey={userkey}
      reason={tokenAsk.reason}
      onClose={() => setTokenAsk(null)}
      onConnected={(l) => {
        const { retry } = tokenAsk;
        setTokenAsk(null);
        if (l) onLinkChange?.(l);
        retry?.();
      }}
    />
  );

  /* Only the newest list read is applied: an older one still in flight (the
     4-second poll during a run, a refresh started before Submit) must not
     land afterwards and put a run's old status back on screen. */
  const loadSeq = useRef(0);
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!userkey) return;
      const seq = ++loadSeq.current;
      if (!quiet) setLoading(true);
      else setRefreshing(true);
      try {
        const fresh = await getJson(`${API_BASE_URL}for-you/test-runs?userkey=${encodeURIComponent(userkey)}&filter=${filter}`);
        if (seq !== loadSeq.current) return;
        setData(fresh);
        setError("");
      } catch (e) {
        if (seq === loadSeq.current) setError(e.message);
      } finally {
        if (seq === loadSeq.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [userkey, filter],
  );

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (refreshSeq) load({ quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSeq]);

  /* While a run is queued or running, keep the list fresh. */
  const rows = (data?.groups || []).flatMap((g) => g.rows);
  const anyActive = rows.some((r) => ["queued", "running"].includes(r.local?.run_status));
  useEffect(() => {
    if (!anyActive) return undefined;
    const t = setInterval(() => load({ quiet: true }), 4000);
    return () => clearInterval(t);
  }, [anyActive, load]);

  const startExecute = async (row, variables = {}, extra = {}) => {
    setRunVars(null);
    setBusy({ run: row.run.id, label: "Starting the run…", sublabel: `Marking ${row.test.key} as executing in Jira and queueing it in Cortex` });
    try {
      await getJson(`${API_BASE_URL}for-you/test-runs/${encodeURIComponent(row.run.id)}/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userkey, variables,
          build_number: extra.build_number || "",
          planned_versions: versionList(extra.planned_versions),
          affects_versions: versionList(extra.affects_versions),
          fix_versions: versionList(extra.fix_versions),
        }),
      });
    } catch (e) {
      failed(e, `Could not execute ${row.test.key}`, () => startExecute(row, variables, extra));
    } finally {
      setBusy(null);
      load({ quiet: true });
    }
  };

  const onAction = async (kind, row) => {
    if (kind === "execute") {
      const te = row.execution;
      const toDo = (te.status || "").toLowerCase() === "to do";
      const extraFields = [
        ...(toDo && !te.build_number ? [{ name: "build_number", label: "Build Number", hint: `${te.key} has none yet; it is set when the execution starts.` }] : []),
        ...VERSION_FIELDS.filter((f) => toDo && !(te[f.name] || []).length),
      ];
      const vars = row.artifact?.variables || [];
      if (vars.length || extraFields.length) {
        setRunVars({ row, extraFields });
        return;
      }
      startExecute(row);
    } else if (kind === "create_artifact") {
      setBusy({ run: row.run.id, label: "Opening Create Artifact…", sublabel: `Fetching the story and epic of ${row.test.key}` });
      try {
        const s = await getJson(`${API_BASE_URL}for-you/tests/${encodeURIComponent(row.test.key)}/story`);
        onCreateArtifact?.({
          projectKey: s.project_key,
          epicKey: s.epic_key,
          epicSummary: s.epic_summary,
          story: { key: s.story_key, summary: s.story_summary, epic_key: s.epic_key, epic_summary: s.epic_summary },
          xrayTestKeys: [row.test.key],
        });
      } catch (e) {
        setFailure({ title: `Can't create an Artifact for ${row.test.key}`, message: e.message });
      } finally {
        setBusy(null);
      }
    } else if (kind === "watch") {
      setWatching({ executionkey: row.local.executionkey, name: `${row.execution.key} · ${row.test.key}` });
    } else if (kind === "review" || kind === "view") {
      setReview(row);
    }
  };

  const submit = async (row, payload, reload) => {
    setBusy({ run: row.run.id, label: "Submitting to Jira…", sublabel: `Writing the result and evidence to the run of ${row.test.key}` });
    try {
      await getJson(`${API_BASE_URL}for-you/test-runs/${encodeURIComponent(row.run.id)}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userkey, ...payload }),
      });
      /* Show the submitted result at once; the refresh from Jira follows. */
      loadSeq.current += 1; // a read already in flight is older than this
      setData((d) => afterSubmit(d, row.run.id, filter));
      setReview(null);
      load({ quiet: true });
    } catch (e) {
      failed(e, `${row.test.key} was not submitted`, () => submit(row, payload, reload));
      reload?.();
      load({ quiet: true });
    } finally {
      setBusy(null);
    }
  };

  const finish = async (te) => {
    setBusy({ run: null, label: "Finishing the execution…", sublabel: `Moving ${te.key} on in Jira` });
    try {
      await getJson(`${API_BASE_URL}for-you/test-executions/${encodeURIComponent(te.key)}/finish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userkey }),
      });
      load({ quiet: true });
    } catch (e) {
      failed(e, `${te.key} was not finished`, () => finish(te));
    } finally {
      setBusy(null);
    }
  };

  if (review) {
    const pending = ["review", "retry_submit"].includes(review.action);
    return (
      <>
        <ExecutionResultView
          executionkey={review.local.executionkey}
          backLabel="Test Runs"
          onBack={() => {
            setReview(null);
            load({ quiet: true });
          }}
          renderFooter={
            pending
              ? (d, reload) => (
                  <TestRunSubmitPanel
                    row={review}
                    bugFromRun={d?.tests?.[0]?.bug_key || ""}
                    busy={!!busy}
                    onSubmit={(payload) => submit(review, payload, reload)}
                  />
                )
              : undefined
          }
        />
        {busy && <Loader.Overlay variant="guide" label={busy.label} sublabel={busy.sublabel} delay={0} />}
        {failure && <ErrorDialog title={failure.title} message={failure.message} onClose={() => setFailure(null)} />}
        {tokenDialog}
      </>
    );
  }

  const groups = data?.groups || [];
  return (
    <div className="space-y-4">
      <div role="tablist" aria-label="Filter test runs" className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={[
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ring-1 ring-inset",
              filter === f.id ? "bg-brand-50 text-brand-700 ring-brand-300" : "bg-card text-slate-600 ring-slate-200 hover:bg-slate-50",
            ].join(" ")}
          >
            {f.label}
            {data?.counts && <span className="rounded-full bg-slate-100 px-1.5 text-[0.6875rem] text-slate-600">{data.counts[f.id] ?? 0}</span>}
          </button>
        ))}
        {refreshing && (
          <span aria-live="polite" className="inline-flex items-center gap-1.5 self-center text-xs text-slate-500">
            <Loader.Inline size={12} /> Updating from Jira…
          </span>
        )}
      </div>

      {error && <p role="alert" className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
      {data && groups.length === 0 && <p className="text-sm text-slate-500">No test runs here.</p>}
      {groups.length > 0 && (
        <ul className="space-y-3">
          {groups.map((g) => (
            <Group key={g.execution.key} g={g} busyRun={busy?.run} onAction={onAction} onFinish={finish} />
          ))}
        </ul>
      )}

      {runVars && (
        <RunVariablesDialog
          title={`Values for ${runVars.row.test.key}`}
          tests={[{ key: runVars.row.test.key, summary: runVars.row.test.summary, variables: runVars.row.artifact?.variables || [] }]}
          extraFields={runVars.extraFields}
          userkey={userkey}
          onCancel={() => setRunVars(null)}
          onExecute={(values, extra) => startExecute(runVars.row, values, extra)}
        />
      )}
      {watching && (
        <LiveRunModal
          ex={watching}
          onClose={() => {
            setWatching(null);
            load({ quiet: true });
          }}
          onStop={(ex) => fetch(`${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}/cancel`, { method: "POST" }).catch(() => {})}
        />
      )}
      {failure && <ErrorDialog title={failure.title} message={failure.message} onClose={() => setFailure(null)} />}
      {tokenDialog}
      {(loading || busy) && (
        <Loader.Overlay
          variant="guide"
          label={busy ? busy.label : "Loading your test runs…"}
          sublabel={busy ? busy.sublabel : "Fetching the test runs assigned to you, and their executions and Artifacts"}
          delay={0}
        />
      )}
    </div>
  );
}
